import {
  convertPaymentTypeIdToName,
  CreateTransaction,
  IPayment,
  IPaymentLine,
  IPaymentQueueLine,
  paymentNameFormatter,
  PaymentStatus,
  PaymentType,
  PaymentTypeIds,
} from '@getgreenline/homi-shared'
import { CreateBarcodeValidation } from '@getgreenline/homi-shared/dist/models/Payments/CreatePaymentLine'
import {
  convertCentsToLoyaltyPoints,
  convertLoyaltyPointsToCents,
  LoyaltyModels,
} from '@getgreenline/loyalties'
import { CurrentCompanyStore } from '../stores/CurrentCompanyStore'
import {
  BlazepayEnums,
  BlazepayPaymentModels,
  BlazepayPaymentOptionModels,
  Payments,
} from '@getgreenline/payments'
import { message } from 'antd-v4'
import { parseErrorMsg } from './helpers'
import { ObservableMap } from 'mobx'
import { getPaymentOptionsWithCache } from './paymentOptionsCache'

export const getTotalProductsToValidate = <T extends IPaymentQueueLine | IPaymentLine>(
  barcodeValidation: CreateBarcodeValidation<T>,
): number => {
  return barcodeValidation.validationLines.filter(
    (item) => item.isValidationRequired && item.validationLine.productId,
  ).length
}

export const paymentHasExistingLoyaltyLine = (payment: Payments.CreatePaymentV2) => {
  const loyaltyTransaction = payment.transactions.find(
    (transaction) => transaction.paymentTypeId === PaymentTypeIds.loyalty,
  )
  return loyaltyTransaction !== undefined
}

export const removeCustomerAndLoyaltyDiscounts = (payment: Payments.CreatePaymentV2): void => {
  payment.removeCustomer()

  const loyaltyBundleDiscountLines = payment.bundleCartDiscountLines
    .map((item, index) => (item.referenceDiscount?.loyaltyCost !== 0 ? index : -1))
    .filter((index) => index !== -1)

  const loyaltyNonBundleDiscountLines = payment.nonBundleCartDiscountLines
    .map((item, index) => (item.referenceDiscount?.loyaltyCost !== 0 ? index : -1))
    .filter((index) => index !== -1)

  payment.removeBundleCartDiscount(loyaltyBundleDiscountLines)
  payment.removeNonBundleCartDiscount(loyaltyNonBundleDiscountLines)

  for (const paymentLine of payment.paymentLines) {
    if (paymentLine.loyaltyCost) paymentLine.removeDiscount()
  }

  payment.addAutomaticProductDiscounts()
}

export const setMaxAllowableLoyaltyInCents = (
  payment: Payments.CreatePaymentV2,
  inputLoyaltyInCents: number,
  currentCompanyStore?: CurrentCompanyStore,
) => {
  const creditBalance = payment.customer?.creditBalance || 0
  const loyaltyDiscountPoints = payment.loyaltyDiscountPoints || 0
  const customerLoyaltyBalance = creditBalance - loyaltyDiscountPoints
  const customerLoyaltyInCents = convertLoyaltyPointsToCents(
    customerLoyaltyBalance,
    currentCompanyStore?.loyaltyPointsMultiplier,
  )

  const lineSumExceptLoyalty = payment.transactions.reduce(
    (acc, trx) => (trx.paymentTypeId === PaymentTypeIds.loyalty ? acc : acc + trx.amount),
    0,
  )

  let totalMinusLoyalty = Math.min(payment.total - lineSumExceptLoyalty)
  if (totalMinusLoyalty < 0) {
    totalMinusLoyalty = 0
  }
  const eligibleLoyaltyInCents = Math.min(
    ...[inputLoyaltyInCents, customerLoyaltyInCents, totalMinusLoyalty],
  )
  return eligibleLoyaltyInCents
}

export const getLoyaltyAsCents = (
  creditBalance: number,
  currentCompanyStore: CurrentCompanyStore,
) => {
  const loyaltyInCents = convertLoyaltyPointsToCents(
    creditBalance,
    currentCompanyStore.loyaltyPointsMultiplier,
  )
  return loyaltyInCents
}

export const calculateMaxAllowableLoyaltyAmount = (
  payment: Payments.CreatePaymentV2,
  inputLoyaltyAmount: number,
  currentCompanyStore?: CurrentCompanyStore,
) => {
  const creditBalance = payment.customer?.creditBalance || 0
  const loyaltyDiscountPoints = payment.loyaltyDiscountPoints || 0
  const customerCreditBalance = creditBalance - loyaltyDiscountPoints
  const customerLoyaltyInCents = convertLoyaltyPointsToCents(
    customerCreditBalance,
    currentCompanyStore?.loyaltyPointsMultiplier,
  )

  const lineSumExceptLoyalty = payment.transactions.reduce(
    (acc, trx) => (trx.paymentTypeId === PaymentTypeIds.loyalty ? acc : acc + trx.amount),
    0,
  )

  let totalMinusLoyalty = Math.min(payment.total - lineSumExceptLoyalty)

  if (totalMinusLoyalty < 0) {
    totalMinusLoyalty = 0
  }

  const eligibleLoyaltyAmount = Math.min(
    ...[inputLoyaltyAmount, customerLoyaltyInCents, totalMinusLoyalty],
  )

  return eligibleLoyaltyAmount
}

export const setLoyaltyCentsTransaction = (
  loyaltyInCents: number,
  transaction: CreateTransaction,
  loyaltyPointsMultiplier: number,
) => {
  const convertedLoyaltyPoints = convertCentsToLoyaltyPoints(
    loyaltyInCents,
    loyaltyPointsMultiplier,
  )

  const loyaltyTransactionMetadata: LoyaltyModels.ILoyaltyTransactionMetadata = {
    points: convertedLoyaltyPoints,
    loyaltyPointsMultiplier: loyaltyPointsMultiplier,
    centValue: loyaltyInCents,
  }

  transaction.setAmount(loyaltyInCents)
  transaction.setMetadata(JSON.stringify(loyaltyTransactionMetadata))
}

export const paymentTypeFilterOptions = (
  canUseMerrcoMoneris: boolean,
): Array<{ value: PaymentType; label: string }> => {
  const options: Array<{ value: PaymentType; label: string }> = [
    { value: 'birchmount', label: convertPaymentTypeIdToName(PaymentTypeIds.birchmount) },
    { value: 'cash', label: convertPaymentTypeIdToName(PaymentTypeIds.cash) },
    { value: 'creditCard', label: convertPaymentTypeIdToName(PaymentTypeIds.credit) },
    { value: 'debitCard', label: convertPaymentTypeIdToName(PaymentTypeIds.debit) },
    { value: 'other', label: convertPaymentTypeIdToName(PaymentTypeIds.other) },
    { value: 'etransfer', label: convertPaymentTypeIdToName(PaymentTypeIds.etransfer) },
    { value: 'online', label: convertPaymentTypeIdToName(PaymentTypeIds.online) },
    { value: 'worldpay', label: convertPaymentTypeIdToName(PaymentTypeIds.worldpay) },
    { value: 'loyalty', label: convertPaymentTypeIdToName(PaymentTypeIds.loyalty) },
    { value: 'blazepay', label: convertPaymentTypeIdToName(PaymentTypeIds.blazepay) },
  ]

  const merrcoMonerisOptions: Array<{ value: PaymentType; label: string }> = [
    { value: 'merrco', label: convertPaymentTypeIdToName(PaymentTypeIds.merrco) },
    { value: 'moneris', label: convertPaymentTypeIdToName(PaymentTypeIds.moneris) },
    { value: 'merrcoDataCandy', label: convertPaymentTypeIdToName(PaymentTypeIds.merrcoDataCandy) },
  ]

  if (canUseMerrcoMoneris) {
    options.push(...merrcoMonerisOptions)
  }

  return options
}

export const getActualRemainingAmount = (payment: Payments.CreatePaymentV2) => {
  return payment.actualTotal - payment.totalPaid
}

export const formatPaymentOptionNames = (
  payment: IPayment,
  paymentOptions?:
    | Map<BlazepayEnums.Provider, BlazepayPaymentOptionModels.IPaymentOptionEventOrRestData>
    | ObservableMap<
        BlazepayEnums.Provider,
        BlazepayPaymentOptionModels.IPaymentOptionEventOrRestData
      >,
) => {
  return payment.transactions.reduce<{ [key: number]: string }>((acc, trans) => {
    const defaultPaymentName = paymentNameFormatter(trans.paymentTypeId)
    const provider = BlazepayPaymentModels.getProvider(trans.paymentTypeId)
    if (!provider) {
      return { ...acc, [trans.paymentTypeId]: defaultPaymentName }
    }

    const paymentOption = paymentOptions?.get(provider)
    if (!paymentOption) {
      return { ...acc, [trans.paymentTypeId]: defaultPaymentName }
    }

    const paymentOptionName = paymentOption.attributes.posDisplayName || defaultPaymentName
    return {
      ...acc,
      [trans.paymentTypeId]: paymentOptionName,
    }
  }, {})
}

export const formatPaymentOptionNameByTypeId = (
  paymentTypeId: PaymentTypeIds,
  paymentOptions?: Map<
    BlazepayEnums.Provider,
    BlazepayPaymentOptionModels.IPaymentOptionEventOrRestData
  >,
) => {
  const defaultPaymentName = paymentNameFormatter(paymentTypeId)
  const provider = BlazepayPaymentModels.getProvider(paymentTypeId)

  if (!provider) return defaultPaymentName

  const paymentOption = paymentOptions?.get(provider)

  if (!paymentOption) return defaultPaymentName

  return paymentOption.attributes.posDisplayName || defaultPaymentName
}

export const getPaymentOptions = async (companyId: number, locationId: number) => {
  const merchantId = `blc-${companyId}-${locationId}`

  const paymentOptions = getPaymentOptionsWithCache({
    merchantId,
  })

  return new Map(
    paymentOptions
      .filter(
        (paymentOption) =>
          paymentOption.attributes.availableCountries.includes(BlazepayEnums.CountryCode.CA) &&
          paymentOption.attributes.posAvailable,
      )
      .map((paymentOption) => [paymentOption.attributes.provider, paymentOption]),
  )
}

export const getReceiptPaymentOptionNames = async ({
  companyId,
  locationId,
  payment,
  paymentOptionNames,
}: {
  companyId: number
  locationId: number
  payment: IPayment
  paymentOptionNames?: { [key: number]: string }
}): Promise<{ [key: number]: string } | undefined> => {
  if (paymentOptionNames || !payment.completeDate) {
    return paymentOptionNames
  }

  const paymentOptions = await getPaymentOptions(companyId, locationId)
  return formatPaymentOptionNames(payment, paymentOptions)
}

export const reduceTipPaymentLines = (payment: IPayment) => {
  if (!payment.paymentLines) {
    return payment
  }

  const tipPaymentLines: IPaymentLine[] = []
  const otherPaymentLines: IPaymentLine[] = []

  for (const paymentLine of payment.paymentLines) {
    if (paymentLine.productName === 'tip') {
      tipPaymentLines.push(paymentLine)
    } else {
      otherPaymentLines.push(paymentLine)
    }
  }

  if (tipPaymentLines.length < 2) {
    return payment
  }

  const tipsTotal = tipPaymentLines.reduce((acc, paymentLine) => acc + paymentLine.price, 0)

  return {
    ...payment,
    paymentLines: [...otherPaymentLines, { ...tipPaymentLines[0], price: tipsTotal }],
  }
}

export const hasBlazepayTransactionAndSaleIsCompleted = (payment: IPayment) => {
  const isPaymentCompleted = payment.status === PaymentStatus.COMPLETED

  if (!isPaymentCompleted) return false

  const isBlazepayTransaction = payment.transactions.some(
    (t) => t.paymentTypeId === PaymentTypeIds.blazepay,
  )

  if (!isBlazepayTransaction) return false

  return true
}
